import { DateTime } from "luxon"

import { Allergen } from "../../../admin/products/productsTypes"
import { DaysOfWeek, DaysOfWeekType } from "../menuPlanningTypes"

import { parseAllergens, toDishParameterType } from "../templates/helpers"
import {
    DishParameter,
    DishParameterKey,
    DishParameterType,
    MenuTemplateDetails,
    MenuTemplateDish,
} from "../templates/menuTemplatesTypes"
import {
    ThemeDish,
    WeekCategoryRows,
    WeekDishField,
    WeekDishRow,
    WeekMenuCellState,
    WeekMenuCellValues,
    WeekMenuPhoto,
    WEEK_DAY_COUNT,
} from "./weekMenuTypes"

/** Weekday of each table column, Monday first, matching the template availability weekdays. */
const WEEK_DAY_ORDER: ReadonlyArray<DaysOfWeekType> = [
    DaysOfWeek.MONDAY,
    DaysOfWeek.TUESDAY,
    DaysOfWeek.WEDNESDAY,
    DaysOfWeek.THURSDAY,
    DaysOfWeek.FRIDAY,
    DaysOfWeek.SATURDAY,
    DaysOfWeek.SUNDAY,
]

// TODO: fetch the marketing tags from the API once it exists; these are English-only placeholders.
export const MARKETING_TAG_OPTIONS: ReadonlyArray<string> = [
    "Vegan",
    "Spicy",
    "Chef's choice",
    "Local farm",
    "New this week",
]

let themeDishCounter = 0

export function newThemeDish(): ThemeDish {
    themeDishCounter += 1
    return {
        id: `theme-dish-${themeDishCounter}`,
        values: emptyCellValues(),
    }
}

const THEME_OPTIONAL_FIELD: WeekDishField = {
    isShown: true,
    type: DishParameterType.OPTIONAL,
    defaultValue: "",
}

/** A synthetic row config for theme dishes: every field editable, nothing mandatory or prefilled. */
export function themeDishRow(name: string): WeekDishRow {
    return {
        key: "theme-dish",
        name,
        availableDayIndexes: [],
        description: THEME_OPTIONAL_FIELD,
        price: THEME_OPTIONAL_FIELD,
        priceSameAllWeek: false,
        allergens: THEME_OPTIONAL_FIELD,
        defaultAllergens: [],
        marketingTag: THEME_OPTIONAL_FIELD,
        image: THEME_OPTIONAL_FIELD,
    }
}

export function weekKeyFor(weekStart: DateTime): string {
    return `${weekStart.weekYear}-W${weekStart.weekNumber}`
}

export function weekDayDates(weekStart: DateTime): ReadonlyArray<DateTime> {
    return Array.from({ length: WEEK_DAY_COUNT }, (_, index) => weekStart.plus({ days: index }))
}

function findParameter(dish: MenuTemplateDish, key: DishParameterKey): DishParameter | undefined {
    return dish.parameters.find((parameter) => parameter.key === key)
}

function toWeekDishField(parameter: DishParameter | undefined): WeekDishField {
    return {
        isShown: parameter !== undefined,
        type: toDishParameterType(parameter?.type) ?? DishParameterType.OPTIONAL,
        defaultValue: parameter?.defaultValue ?? "",
    }
}

function toWeekDishRow(dish: MenuTemplateDish, key: string, allergenOptions: ReadonlyArray<Allergen>): WeekDishRow {
    const price = findParameter(dish, DishParameterKey.PRICE)
    const allergens = toWeekDishField(findParameter(dish, DishParameterKey.ALLERGENS))
    const weekDays = dish.availability?.weekDays ?? [...WEEK_DAY_ORDER]

    return {
        key,
        name: dish.name,
        availableDayIndexes: WEEK_DAY_ORDER.map((day, index) => (weekDays.includes(day) ? index : -1)).filter(
            (index) => index >= 0,
        ),
        description: toWeekDishField(findParameter(dish, DishParameterKey.DESCRIPTION)),
        price: toWeekDishField(price),
        priceSameAllWeek: price?.sameAllWeek ?? false,
        allergens,
        defaultAllergens: parseAllergens(allergens.defaultValue, allergenOptions),
        marketingTag: toWeekDishField(findParameter(dish, DishParameterKey.MARKETING_TAG)),
        image: toWeekDishField(findParameter(dish, DishParameterKey.IMAGE)),
    }
}

export function buildWeekRows(
    details: MenuTemplateDetails,
    allergenOptions: ReadonlyArray<Allergen>,
): ReadonlyArray<WeekCategoryRows> {
    return details.categories.map((category, categoryIndex) => ({
        name: category.name,
        dishes: category.dishes.map((dish, dishIndex) =>
            toWeekDishRow(dish, `${categoryIndex}-${dishIndex}`, allergenOptions),
        ),
    }))
}

export function isFieldLocked(field: WeekDishField): boolean {
    return field.isShown && field.type === DishParameterType.LOCKED
}

const EMPTY_DETAILS = { ingredients: "", nutrition: "", co2: "" }

/** Cell values a slot starts with: the template defaults of its shown fields. */
export function defaultCellValues(row: WeekDishRow): WeekMenuCellValues {
    return {
        title: "",
        details: EMPTY_DETAILS,
        description: row.description.isShown ? row.description.defaultValue : "",
        allergens: row.allergens.isShown ? row.defaultAllergens : [],
        price: row.price.isShown ? row.price.defaultValue : "",
        marketingTag: row.marketingTag.isShown ? row.marketingTag.defaultValue : "",
        photo:
            row.image.isShown && row.image.defaultValue ? { name: row.image.defaultValue, isFromTemplate: true } : null,
    }
}

export function emptyCellValues(): WeekMenuCellValues {
    return {
        title: "",
        description: "",
        allergens: [],
        price: "",
        marketingTag: "",
        photo: null,
        details: EMPTY_DETAILS,
    }
}

function isEmptyCellValues(values: WeekMenuCellValues): boolean {
    return (
        values.title.trim() === "" &&
        values.description.trim() === "" &&
        values.price.trim() === "" &&
        values.marketingTag.trim() === "" &&
        values.allergens.length === 0 &&
        values.photo === null &&
        values.details.ingredients === "" &&
        values.details.nutrition === "" &&
        values.details.co2 === ""
    )
}

/** True when a partial update changes nothing, so an untouched cell is not marked as started. */
export function isSameCellValues(current: WeekMenuCellValues, values: Partial<WeekMenuCellValues>): boolean {
    if (values.title !== undefined && values.title !== current.title) return false
    if (values.description !== undefined && values.description !== current.description) return false
    if (values.price !== undefined && values.price !== current.price) return false
    if (values.marketingTag !== undefined && values.marketingTag !== current.marketingTag) return false
    if (values.photo !== undefined && values.photo?.url !== current.photo?.url) return false
    if (values.photo !== undefined && values.photo?.name !== current.photo?.name) return false
    if (values.details !== undefined) {
        if (values.details.ingredients !== current.details.ingredients) return false
        if (values.details.nutrition !== current.details.nutrition) return false
        if (values.details.co2 !== current.details.co2) return false
    }
    if (values.allergens !== undefined) {
        if (values.allergens.length !== current.allergens.length) return false
        if (values.allergens.some((allergen, index) => allergen.id !== current.allergens[index].id)) return false
    }
    return true
}

function hasOwnContent(values: WeekMenuCellValues, defaults: WeekMenuCellValues): boolean {
    return (
        values.title.trim() !== "" ||
        values.description.trim() !== defaults.description.trim() ||
        values.price.trim() !== defaults.price.trim() ||
        values.marketingTag.trim() !== defaults.marketingTag.trim() ||
        values.allergens.length !== defaults.allergens.length ||
        values.photo?.isFromTemplate === false
    )
}

function hasTemplateDefaults(defaults: WeekMenuCellValues): boolean {
    return (
        defaults.description.trim() !== "" ||
        defaults.price.trim() !== "" ||
        defaults.marketingTag.trim() !== "" ||
        defaults.allergens.length > 0 ||
        defaults.photo !== null
    )
}

function isMandatoryMissing(row: WeekDishRow, values: WeekMenuCellValues): boolean {
    return (
        values.title.trim() === "" ||
        isMandatoryFieldMissing(row.description, values.description) ||
        isMandatoryFieldMissing(row.price, values.price) ||
        isMandatoryFieldMissing(row.marketingTag, values.marketingTag) ||
        isMandatoryAllergensMissing(row.allergens, values.allergens) ||
        isMandatoryPhotoMissing(row.image, values.photo)
    )
}

/** The mandatory-missing highlight only appears once the kitchen has begun filling in the dish. */
export function cellStateFor(row: WeekDishRow, values: WeekMenuCellValues, isTouched: boolean): WeekMenuCellState {
    // A cleared cell is empty even on a row whose template carries defaults.
    if (!isTouched && isEmptyCellValues(values)) return WeekMenuCellState.EMPTY
    const defaults = defaultCellValues(row)
    const started = isTouched || hasOwnContent(values, defaults)
    if (!started) {
        return hasTemplateDefaults(defaults) ? WeekMenuCellState.TEMPLATE_DEFAULT : WeekMenuCellState.EMPTY
    }
    return isMandatoryMissing(row, values) ? WeekMenuCellState.NEEDS_FILL : WeekMenuCellState.FILLED
}

/** True when the template prefills any of the row's fields, which the reset action restores. */
export function rowHasTemplateDefaults(row: WeekDishRow): boolean {
    return (
        row.description.defaultValue !== "" ||
        row.price.defaultValue !== "" ||
        row.marketingTag.defaultValue !== "" ||
        row.allergens.defaultValue !== "" ||
        row.image.defaultValue !== ""
    )
}

function isMandatoryAndShown(field: WeekDishField): boolean {
    return field.isShown && field.type === DishParameterType.MANDATORY
}

export function isMandatoryFieldMissing(field: WeekDishField, value: string): boolean {
    return isMandatoryAndShown(field) && value.trim() === ""
}

export function isMandatoryAllergensMissing(field: WeekDishField, allergens: ReadonlyArray<Allergen>): boolean {
    return isMandatoryAndShown(field) && allergens.length === 0
}

export function isMandatoryPhotoMissing(field: WeekDishField, photo: WeekMenuPhoto | null): boolean {
    return isMandatoryAndShown(field) && photo === null
}

// TODO: upload the picture and keep the returned URL once the week menu API exists.
/**
 * The picture is kept locally until the week menu API exists. The object URL is never revoked:
 * copying a dish shares the same URL across cells and weeks, so freeing it for one cell would
 * break the picture in the others. The URLs live until the page unloads.
 */
export function localPhotoFromFile(file: File): WeekMenuPhoto {
    return { url: URL.createObjectURL(file), name: file.name, isFromTemplate: false }
}
